import Swal from 'sweetalert2'
import { toast } from 'react-toastify'

import { escapeHtml } from '../../../../shared/helpers'
import http from '../../../../shared/http'
import { TApi, TRoles } from '../../../../shared/const'
import { lastNameMatches } from './removeStudent'

/*
 * Konto einer Lehrkraft löschen (Anforderung #239): irrtümlich als Lehrperson
 * angemeldete Schüler:innen, Lehrkräfte, die nicht mehr im Schuldienst sind.
 *
 * Derselbe Ablauf wie bei Schüler:innen (`removeStudent.js`): erst die
 * Vorschau vom Server, dann das Fenster — welche Junior Companies betroffen
 * sind, wer die Betreuung übernimmt, was gelöscht wird — und zum Bestätigen
 * den Nachnamen eintippen. Ist die Person die einzige Lehrkraft einer Junior
 * Company, zeigt ein eigenes Fenster ohne Löschknopf den Ausweg.
 *
 * **Geschützt wird im Backend** (`services/studentRemoval`, `removeTeacher`).
 */

/** Wer den Eintrag sieht: Landes- und Bundesadmin, nur bei Lehrkräften. */
export const canRemoveTeacher = (viewerRole, user) =>
	[TRoles.ADMIN, TRoles.FEDERAL_ADMIN].includes(viewerRole) && user?.role === TRoles.TEACHER

const fullName = person => [person?.firstName, person?.lastName].filter(Boolean).join(' ')

const messageFor = (t, item, preview) => {
	const name = fullName(preview.teacher)
	switch (item.code) {
		case 'ONLY_TEACHER':
			return t('teacherRemovalOnlyTeacher', { name, company: item.company || '' })
		case 'SUPERVISION_PASSES':
			return t('teacherRemovalSupervisionPasses', {
				name,
				company: item.company || '',
				successor: item.successor || ''
			})
		case 'NOT_A_TEACHER':
			return t('teacherRemovalNotATeacher')
		default:
			return item.message
	}
}

const sum = values => Object.values(values || {}).reduce((total, value) => total + (Number(value) || 0), 0)

/** Was gelöscht wird — eine Zeile je Art, mit Zahl, nur was es gibt. */
export const teacherDeletedLines = (t, preview) => {
	const d = preview.deleted || {}
	const lines = [
		[d.Supervision, t('teacherRemovalItemSupervision')],
		[d.TeacherAssignRequests, t('teacherRemovalItemRequests')],
		[d.Notification, t('studentRemovalItemNotifications')],
		[d.invitations, t('studentRemovalItemInvitations')],
		[d.userComments, t('studentRemovalItemComments')],
		[
			(d.documents || 0) +
				(d.CourseProgress || 0) +
				(d.CourseLessonProgress || 0) +
				(d.Permission || 0) +
				(d.AdminTraining || 0) +
				(d.AdminReminderSetting || 0),
			t('studentRemovalItemSettings')
		]
	]
		.filter(([count]) => Number(count) > 0)
		.map(([count, label]) => `${label}: ${count}`)

	return [t('studentRemovalItemAccount'), ...lines]
}

export const teacherConfirmationHtml = (t, preview) => {
	const name = escapeHtml(fullName(preview.teacher))
	const school = preview.school ? escapeHtml(preview.school.name) : escapeHtml(t('teacherRemovalNoSchool'))
	const companies = (preview.companies || []).map(c => `„${escapeHtml(c.name)}“`)
	const warnings = (preview.warnings || []).map(item => escapeHtml(messageFor(t, item, preview)))

	return [
		`<div style="text-align:left">`,
		`<p><b>${name}</b> · ${school}</p>`,
		`<p>${escapeHtml(t('teacherRemovalCompanies'))} ${
			companies.length ? companies.join(', ') : escapeHtml(t('teacherRemovalNoCompanies'))
		}</p>`,
		`<p style="margin-bottom:4px">${escapeHtml(t('studentRemovalDeletedHeading'))}</p>`,
		`<ul style="margin:0 0 12px 18px;padding:0">`,
		...teacherDeletedLines(t, preview).map(line => `<li>${escapeHtml(line)}</li>`),
		`</ul>`,
		`<p>${escapeHtml(t('teacherRemovalKept', { count: sum(preview.detached) }))}</p>`,
		...(warnings.length
			? [
					`<div style="background:#fff4e5;border-left:4px solid #f0a020;padding:8px 10px;margin:10px 0">`,
					...warnings.map(text => `<p style="margin:4px 0">${text}</p>`),
					`</div>`
			  ]
			: []),
		`<p><b>${escapeHtml(t('studentRemovalFinal'))}</b></p>`,
		`<p>${escapeHtml(t('studentRemovalTypeLastName', { lastName: preview.teacher?.lastName || '' }))}</p>`,
		`</div>`
	].join('')
}

/**
 * Der ganze Ablauf. `onDone` läuft nur nach erfolgreichem Löschen.
 * @returns {Promise<boolean>} ob gelöscht wurde
 */
export const removeTeacherAccount = async ({ t, user, onDone }) => {
	let preview
	try {
		;({ data: preview } = await http.get(TApi.ADMIN_TEACHER_REMOVAL_PREVIEW(user.id)))
	} catch {
		return false // Meldung kommt aus shared/http
	}

	if (preview.blockers?.length) {
		await Swal.fire({
			title: t('studentRemovalBlockedTitle'),
			html: `<div style="text-align:left">${preview.blockers
				.map(item => `<p>${escapeHtml(messageFor(t, item, preview))}</p>`)
				.join('')}</div>`,
			icon: 'error',
			confirmButtonText: t('OK')
		})
		return false
	}

	const lastName = preview.teacher?.lastName || ''
	const { isConfirmed } = await Swal.fire({
		title: t('studentRemovalTitle'),
		html: teacherConfirmationHtml(t, preview),
		icon: 'warning',
		width: 640,
		input: 'text',
		inputPlaceholder: lastName,
		inputAttributes: { autocapitalize: 'off', autocorrect: 'off', autocomplete: 'off' },
		inputValidator: value => (lastNameMatches(value, lastName) ? undefined : t('Input does not match')),
		showCancelButton: true,
		confirmButtonColor: '#d33',
		cancelButtonColor: '#6c757d',
		confirmButtonText: t('studentRemovalConfirm'),
		cancelButtonText: t('Cancel'),
		focusCancel: true
	})

	if (!isConfirmed) return false

	try {
		await http.delete(TApi.ADMIN_REMOVE_TEACHER(user.id))
	} catch {
		return false // Meldung kommt aus shared/http (z. B. 409 mit dem Satz des Servers)
	}

	toast.success(t('studentRemovalDone', { name: fullName(preview.teacher) }))
	if (typeof onDone === 'function') onDone()
	return true
}
